import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';
import TableHeader from 'components/Table/TableHeader';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: [],
  id: 1,
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

function RpShelfLife() {
  const mod = 'rp_shelf_life';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [condition, setCondition] = useState([]);
  const [cboBranch, setCboBranch] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'branch_name', label: 'สาขา', align: 'left', sort: false },
    { id: 'name', label: 'สินค้า', align: 'left', sort: false },
    { id: 'qty_per_day', label: 'จำนวนขายต่อวัน', align: 'right', sort: false },
    { id: 'shelf_life', label: 'จำนวนวันบนชั้น', align: 'right', sort: false },
    { id: 'quantity_balance', label: 'จำนวนคงเหลือสาขา', align: 'right', sort: false },
    { id: 'abc', label: 'CAT ABCสาขา', align: 'center', sort: false },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      date_start: data.date_start,
      date_stop: data.date_stop,
      branch: data.branch,
      id: data.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const setDateRange = (start, stop) => {
    if (find.branch.length > 0) {
      view({
        ...find,
        date_start: Func.dateTimeFormat(start.toDate()),
        date_stop: Func.dateTimeFormat(stop.toDate()),
        page: 1,
      });
    }
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);
    params.append('id', find.id);

    find.branch.forEach((branchId) => {
      params.append('branch[]', branchId);
    });

    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCondition(result.condition);
        setCboBranch(result.branch);
        setUrlExcel(result.excel);
        view({ ...find, branch: [], id: 1 });
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              select
              fullWidth
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (selected.length === cboBranch.length) return 'ทั้งหมด';
                  return cboBranch
                    .filter((item) => selected.includes(item.id))
                    .map((item) => item.name)
                    .join(', ');
                },
              }}
              value={find.branch}
              onChange={(e) => {
                const { value } = e.target;

                if (value.includes('ALL')) {
                  const allSelected = find.branch.length === cboBranch.length;
                  view({
                    ...find,
                    branch: allSelected ? [] : cboBranch.map((item) => item.id),
                    page: 1,
                  });
                } else {
                  view({ ...find, branch: value, page: 1 });
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>
                <Checkbox checked={find.branch.length === cboBranch.length} />
                ทั้งหมด
              </MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={find.branch.indexOf(item.id) > -1} />
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={5}>
            <MDDateRange
              dateStart={find.date_start}
              dateStop={find.date_stop}
              callBack={setDateRange}
              closeDate={() => {
                if (find.branch.length > 0) {
                  view({ ...find, page: 1 });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3} sx={{ mt: 1 }}>
          <MDInput
            fullWidth
            value={find.id}
            defaultValue='ALL'
            select
            onChange={(e) => view({ ...find, id: e.target.value, page: 1 })}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>{t('menu.condition')} :</InputAdornment>
              ),
            }}
          >
            {condition.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </MDInput>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_manage_branch'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader data={initHeader} />
                <TableBody>
                  {record.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={initHeader.length} align='center'>
                        ไม่พบข้อมูล{find.branch.length === 0 ? 'กรุณาเลือกสาขา' : 'ในสาขาที่เลือก'}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell className='nowrap'>{row.branch_name}</TableCell>
                      <TableCell align='left'>
                        <MDBox>{row.sku}</MDBox>
                        <MDBox className='nowrap'>{row.name}</MDBox>
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.qty_per_day}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {row.shelf_life}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.quantity_balance)}
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.abc_branch}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_manage_branch`), t(`menu.rp_shelf_life`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpShelfLife;
